import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Video, VideoOff, Mic, MicOff, PhoneOff, Users,
  Plus, Phone, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Participant({ participant, isLocal }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!participant) return;

    const handleTrack = (track) => {
      if (track.kind === 'video' && videoRef.current) {
        track.attach(videoRef.current);
      } else if (track.kind === 'audio' && audioRef.current && !isLocal) {
        track.attach(audioRef.current);
      }
    };

    const handleTrackRemoved = (track) => {
      if (track.kind === 'video' && videoRef.current) {
        track.detach(videoRef.current);
      } else if (track.kind === 'audio' && audioRef.current) {
        track.detach(audioRef.current);
      }
    };

    // Attach existing tracks
    if (participant.tracks) {
      participant.tracks.forEach((pub) => {
        if (pub.track) handleTrack(pub.track);
      });
    }

    // Listen for new tracks
    participant.on('trackSubscribed', handleTrack);
    participant.on('trackUnsubscribed', handleTrackRemoved);

    // For local participant, attach local tracks directly
    if (isLocal && participant.videoTracks) {
      participant.videoTracks.forEach((pub) => {
        if (pub.track && videoRef.current) {
          pub.track.attach(videoRef.current);
        }
      });
    }

    return () => {
      participant.removeAllListeners('trackSubscribed');
      participant.removeAllListeners('trackUnsubscribed');
    };
  }, [participant, isLocal]);

  const identity = participant?.identity || 'Unknown';
  const displayName = identity.split('_')[0];

  return (
    <div className="relative rounded-xl overflow-hidden bg-muted border border-border aspect-video" data-testid={`participant-${identity}`}>
      <video ref={videoRef} autoPlay playsInline muted={isLocal}
        className="w-full h-full object-cover" />
      <audio ref={audioRef} autoPlay />
      <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-black/60 text-white text-xs">
        {displayName}{isLocal ? ' (You)' : ''}
      </div>
    </div>
  );
}

export default function VideoCall({ onBack }) {
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [roomName, setRoomName] = useState('');
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [activeRooms, setActiveRooms] = useState([]);
  const [callType, setCallType] = useState(null); // null | 'create' | 'join'
  const twilioVideoRef = useRef(null);

  // Lazy-load twilio-video
  const getTwilioVideo = useCallback(async () => {
    if (!twilioVideoRef.current) {
      twilioVideoRef.current = await import('twilio-video');
    }
    return twilioVideoRef.current;
  }, []);

  useEffect(() => {
    fetchActiveRooms();
    return () => {
      if (room) room.disconnect();
    };
  }, []);

  const fetchActiveRooms = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/video/rooms`, { withCredentials: true });
      setActiveRooms(data);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
  };

  const joinRoom = async (name) => {
    if (!name) {
      toast.error('Please enter a room name');
      return;
    }
    setConnecting(true);

    try {
      // Get token
      const { data: tokenData } = await axios.post(
        `${API_URL}/api/video/token`,
        { room_name: name },
        { withCredentials: true }
      );

      const Video = await getTwilioVideo();
      const connectedRoom = await Video.connect(tokenData.token, {
        name: name,
        audio: true,
        video: { width: 640, height: 480 },
        dominantSpeaker: true,
      });

      setRoom(connectedRoom);

      // Handle existing participants
      connectedRoom.participants.forEach((p) => {
        setParticipants((prev) => [...prev, p]);
      });

      connectedRoom.on('participantConnected', (p) => {
        setParticipants((prev) => [...prev, p]);
        toast.success(`${p.identity.split('_')[0]} joined`);
      });

      connectedRoom.on('participantDisconnected', (p) => {
        setParticipants((prev) => prev.filter((x) => x.sid !== p.sid));
        toast.info(`${p.identity.split('_')[0]} left`);
      });

      connectedRoom.on('disconnected', () => {
        setRoom(null);
        setParticipants([]);
      });
    } catch (err) {
      console.error('Join room error:', err);
      toast.error('Failed to join video room');
    } finally {
      setConnecting(false);
    }
  };

  const createRoom = async () => {
    if (!roomName.trim()) {
      toast.error('Enter a room name');
      return;
    }
    setConnecting(true);

    try {
      await axios.post(
        `${API_URL}/api/video/rooms`,
        { room_name: roomName.trim(), room_type: 'group' },
        { withCredentials: true }
      );

      await joinRoom(roomName.trim());
    } catch (err) {
      console.error('Create room error:', err);
      toast.error('Failed to create room');
      setConnecting(false);
    }
  };

  const leaveRoom = () => {
    if (room) {
      room.disconnect();
      setRoom(null);
      setParticipants([]);
    }
  };

  const toggleVideo = () => {
    if (room) {
      room.localParticipant.videoTracks.forEach((pub) => {
        if (isVideoEnabled) {
          pub.track.disable();
        } else {
          pub.track.enable();
        }
      });
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = () => {
    if (room) {
      room.localParticipant.audioTracks.forEach((pub) => {
        if (isAudioEnabled) {
          pub.track.disable();
        } else {
          pub.track.enable();
        }
      });
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  // In-call UI
  if (room) {
    return (
      <div className="h-full flex flex-col" data-testid="video-call-active">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Video className="w-5 h-5 text-[#FF6F61]" />
            <span className="text-foreground font-medium">{room.name}</span>
            <span className="text-xs text-muted-foreground">
              {participants.length + 1} participant{participants.length !== 0 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="flex-1 p-4 overflow-auto">
          <div className={`grid gap-4 h-full ${
            participants.length === 0 ? 'grid-cols-1 max-w-2xl mx-auto' :
            participants.length <= 1 ? 'grid-cols-2' :
            participants.length <= 3 ? 'grid-cols-2' :
            'grid-cols-3'
          }`}>
            <Participant participant={room.localParticipant} isLocal />
            {participants.map((p) => (
              <Participant key={p.sid} participant={p} />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 p-4 border-t border-border">
          <Button variant="outline" size="icon"
            className={`rounded-full w-12 h-12 ${isVideoEnabled ? 'border-border text-foreground' : 'bg-red-500/20 border-red-500 text-red-400'}`}
            onClick={toggleVideo} data-testid="toggle-video-btn">
            {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
          </Button>
          <Button variant="outline" size="icon"
            className={`rounded-full w-12 h-12 ${isAudioEnabled ? 'border-border text-foreground' : 'bg-red-500/20 border-red-500 text-red-400'}`}
            onClick={toggleAudio} data-testid="toggle-audio-btn">
            {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
          </Button>
          <Button className="rounded-full w-12 h-12 bg-red-500 hover:bg-red-600"
            onClick={leaveRoom} data-testid="leave-room-btn">
            <PhoneOff className="w-5 h-5 text-white" />
          </Button>
        </div>
      </div>
    );
  }

  // Lobby UI
  return (
    <div className="p-6 space-y-6" data-testid="video-call-lobby">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        )}
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground">Video Calls</h2>
          <p className="text-sm text-muted-foreground">Start or join a video consultation</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-6 rounded-xl bg-muted border border-border space-y-4">
          <div className="flex items-center gap-3">
            <Plus className="w-5 h-5 text-[#FF6F61]" />
            <h3 className="text-foreground font-medium">Create Room</h3>
          </div>
          <Input
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter room name"
            className="glass-card border-border text-foreground"
            data-testid="room-name-input"
          />
          <Button className="btn-primary w-full" onClick={createRoom} disabled={connecting} data-testid="create-room-btn">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
            {connecting ? 'Connecting...' : 'Start Video Call'}
          </Button>
        </div>

        <div className="p-6 rounded-xl bg-muted border border-border space-y-4">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-[#A8E6CF]" />
            <h3 className="text-foreground font-medium">Join Room</h3>
          </div>
          <Input
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="Enter room name to join"
            className="glass-card border-border text-foreground"
            data-testid="join-room-name-input"
          />
          <Button variant="outline" className="w-full border-border text-foreground hover:bg-muted"
            onClick={() => joinRoom(roomName)} disabled={connecting} data-testid="join-room-btn">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
            {connecting ? 'Connecting...' : 'Join Room'}
          </Button>
        </div>
      </div>

      {/* Active Rooms */}
      {activeRooms.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active Rooms</h3>
          {activeRooms.map((r) => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-muted border border-border">
              <div>
                <p className="text-foreground font-medium">{r.room_name}</p>
                <p className="text-xs text-muted-foreground">Created by {r.creator_name}</p>
              </div>
              <Button variant="outline" size="sm" className="border-border text-foreground"
                onClick={() => joinRoom(r.room_name)} data-testid={`join-active-${r.room_name}`}>
                Join
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
